import React, { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import _ from "lodash";
import SearchComponent from "../../../components/dashboard-components/SearchComponent";
import { useUserStore } from "../../../store/useUser";
import UsersModal from "../../../components/dashboard-components/users/UsersModal";
import { useIndividualCustomer } from "../../../sevices/hooks/account";
import CustomerTable, { ActionType } from "../../../components/customerTable";

// Define proper types for user data
interface UserData {
  userId: string;
  fullName?: string;
  businessName?: string;
  email: string;
  refereeCode: string;
  invitationCode?: string;
  profileStatus: string;
  kycstatus: string;
  invitedBy?: string;
  transactionCount: number;
  createdAt: string;
  [key: string]: any; // For any additional properties
}

interface PaginatedResponse {
  data: UserData[];
  totalPages: number;
  currentPage: number;
}

const Individual: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [pageSize, setPageSize] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>(
    searchParams.get("search") || ""
  );

  // User store for user details modal
  const setUserDetailsModal = useUserStore((state) => state.setShowUserDetails);
  const showUserDetailsModal = useUserStore((state) => state.showUserDetails);
  const setUserId = useUserStore((state) => state.setSelectedUsersId);

  // Fetch users data
  const {
    data: individualUser,
    isLoading,
    refetch,
  } = useIndividualCustomer({
    pageSize,
    page,
    search: searchTerm,
  });

  // Create a memoized refetch function with useCallback
  const handleRefetch = useCallback(async () => {
    try {
      return await refetch();
    } catch (error) {
      console.error("Failed to refetch user data:", error);
      return null;
    }
  }, [refetch]);

  // Debounced search to prevent excessive API calls
  const debouncedSearch = useCallback(
    _.debounce((term: string) => {
      setSearchTerm(term);
      setPage(1);
    }, 500),
    []
  );

  const handleSearch = useCallback(
    (value: string) => {
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  // Handle action from CustomerTable that needs to be processed at this level
  const handleTableAction = useCallback(
    async (action: ActionType, user: any) => {
      console.log(
        `Action ${action.toLowerCase()} performed on user:`,
        user.userId
      );

      // Different actions might require different handling
      if (
        action === ActionType.Activate ||
        action === ActionType.Suspend ||
        action === ActionType.Delete ||
        action === ActionType.Block
      ) {
        // No need to call refetch here as the CustomerTable will handle it
        // The proper trigger is in the CustomerTable's handleConfirmAction
      }
    },
    []
  );

  // Format data for table consumption
  const tableData = React.useMemo(() => {
    if (!individualUser?.data) return [];
    return individualUser.data;
  }, [individualUser]);

  return (
    <>
      <div className="h-100vh w-full bg-[#FEFEFF] p-5 md:pr-5 md:pl-8">
        <div className="flex flex-row items-center justify-between mt-5">
          <h1 className="font-bold text-2xl text-[#071827] md:pb-2">Users</h1>
        </div>
        <SearchComponent placeholder="Search users" onSearch={handleSearch} />
        <div>
          {/* <CustomerTable
            refetchTable={handleRefetch}
            data={tableData || []}
            isLoading={isLoading}
            page={page}
            totalPages={individualUser?.data?.totalPages || 1}
            onPageChange={setPage}
            searchTerm={searchTerm}
            onAction={handleTableAction}
          /> */}

          <CustomerTable
            refetchTable={handleRefetch}
            data={individualUser?.data?.data || []} // Pass array directly, not nested object
            isLoading={isLoading}
            page={page}
            totalPages={individualUser?.data?.totalPages || 1}
            onPageChange={setPage}
            searchTerm={searchTerm}
            onAction={handleTableAction}
          />
        </div>
      </div>

      {/* User details modal */}
      {showUserDetailsModal && (
        <UsersModal
          isOpen={showUserDetailsModal}
          refetchTable={handleRefetch}
          onClose={() => setUserDetailsModal(false)}
        />
      )}
    </>
  );
};

export default Individual;
